import type { WeeklyChallenge } from '../types';

export const WEEKLY_CHALLENGES: WeeklyChallenge[] = [
  {
    id: 'wc-01',
    weekNumber: 1,
    title: 'Stranger Safety Week',
    theme: 'Knowing the difference between safe and unsafe strangers',
    icon: '🛡️',
    challengeDescription:
      'Together, map your home neighbourhood on paper or on a screen. Mark 3–5 "safe houses" — neighbours, shops, or community spots where your child could go if they felt unsafe or got lost. Walk or drive past at least one of them this week and introduce your child to someone there if possible.',
    talkingPoints: [
      'What makes someone a safe stranger versus an unsafe one?',
      'If you were separated from me in a busy place, what would you do first?',
      'Can you name three trusted adults outside our home you could go to?',
    ],
    kidMission:
      'Memorise one parent\'s phone number by heart. Practise dialling it on a real phone (not just reciting it). Tell a parent when you\'ve done it.',
    parentReflection:
      'Does your child know your full name (not just "Mum" or "Dad"), your phone number, and your home address? This week, quietly test each one.',
    badgeEmoji: '🛡️',
    badgeName: 'Safety Scout',
    targetCount: 3,
    categoryId: 'personal-safety',
  },
  {
    id: 'wc-02',
    weekNumber: 2,
    title: 'Body Autonomy Week',
    theme: 'My body belongs to me — safe touch and unsafe touch',
    icon: '💪',
    challengeDescription:
      'Read or tell a short story together about a child who uses their voice to say "no" to something that feels wrong. You can use a KidWise scenario card as the story, or make one up. Practise saying "NO" loudly together — make it fun, do it three times with increasing volume. Then talk about what to do after saying no.',
    talkingPoints: [
      'Can you tell me the difference between a safe touch and an unsafe touch?',
      'What do you do if someone touches you in a way that feels wrong, even if it\'s someone you know?',
      'Is it ever okay to keep a secret about your body from Mum or Dad?',
    ],
    kidMission:
      'Draw or write about a time you felt uncomfortable and what you did (or wished you\'d done). Share it with a parent if you feel comfortable.',
    parentReflection:
      'Have you used the correct anatomical names for body parts with your child? Research suggests this is one of the most effective protective factors. This week, normalise them in a low-key way.',
    badgeEmoji: '💪',
    badgeName: 'Body Boss',
    targetCount: 3,
    categoryId: 'personal-safety',
  },
  {
    id: 'wc-03',
    weekNumber: 3,
    title: 'Emergency Skills Week',
    theme: 'What to do when something goes wrong fast',
    icon: '🚨',
    challengeDescription:
      'Run a family fire drill. Choose a random time, shout "fire drill!", and time how long it takes everyone to get to your agreed meeting point outside. Set a family record. Then practise calling emergency services — use a disconnected phone to roleplay: "What do you say? What do they ask?"',
    talkingPoints: [
      'What are the three things you do in a fire? Stop, drop — what\'s the third?',
      'When should you call 911 / 112 — can you give me three examples?',
      'If I was hurt and couldn\'t speak, what would you do?',
    ],
    kidMission:
      'Learn your full home address including postcode. Write it on a card and keep it in your school bag.',
    parentReflection:
      'When did you last check your smoke alarms? Do your children know where the fire extinguisher is and how to use it? Make a note of any gaps.',
    badgeEmoji: '🚨',
    badgeName: 'Emergency Ready',
    targetCount: 3,
    categoryId: 'emergency-skills',
  },
  {
    id: 'wc-04',
    weekNumber: 4,
    title: 'Online Safety Basics Week',
    theme: 'The internet is amazing — and needs rules',
    icon: '💻',
    challengeDescription:
      'Sit down together and create your family\'s "Digital House Rules" — a short list (5 rules max) that everyone agrees on. Write or type them up, have everyone sign it, and stick it near the family device. Examples: no screens in bedrooms at night, always tell a parent if something feels wrong online, never share our address.',
    talkingPoints: [
      'If someone online asks for your real name and school, what do you do?',
      'What does it mean if someone you\'ve never met in person says they\'re your friend?',
      'If you saw something scary or confusing online, would you tell me? What would you say?',
    ],
    kidMission:
      'Check your own social media or game profile privacy settings with a parent. Make sure your real name, age, school, and photo are not visible to strangers.',
    parentReflection:
      'Do you know all the apps, games, and platforms your child uses? Spend 10 minutes this week looking at one you\'re not familiar with.',
    badgeEmoji: '💻',
    badgeName: 'Digital Guardian',
    targetCount: 3,
    categoryId: 'online-safety',
  },
  {
    id: 'wc-05',
    weekNumber: 5,
    title: 'Getting Lost Week',
    theme: 'Staying calm and getting found',
    icon: '🗺️',
    challengeDescription:
      'Visit a busy public place — a shopping centre, park, or transport hub. Give your child a short "solo mission": ask a member of staff a question, find the nearest exit sign, or locate a help point. Watch from a distance. Debrief together after on how it felt.',
    talkingPoints: [
      'If you got separated from me here right now, what would you do in the first 60 seconds?',
      'Who are the safest people to ask for help in a public place?',
      'What information would you give someone trying to help find me?',
    ],
    kidMission:
      'Create a small "if I get lost" card for your school bag with: your name, a parent\'s phone number, your home address, and an ICE (In Case of Emergency) contact.',
    parentReflection:
      'Have you practised the "lost" plan at an actual busy location, or only talked about it at home? Real environments create real confidence.',
    badgeEmoji: '🗺️',
    badgeName: 'Wayfinder',
    targetCount: 3,
    categoryId: 'travel-public',
  },
  {
    id: 'wc-06',
    weekNumber: 6,
    title: 'Bullying & Friendship Week',
    theme: 'Knowing the difference — and what to do about both',
    icon: '🤝',
    challengeDescription:
      'Play "Bully or Conflict?" — read out 5 scenarios (you can use KidWise cards or make up your own) and decide together whether each is bullying or a normal friendship conflict, and why the difference matters. Then roleplay one response to each type.',
    talkingPoints: [
      'Has anyone at school ever made you feel left out or upset on purpose?',
      'What\'s the difference between telling tales and telling a trusted adult about bullying?',
      'If your friend was being bullied, what could you do?',
    ],
    kidMission:
      'This week, do one kind thing for someone who might be feeling left out — at school, in class, or in a club. Tell a parent about it at the end of the week.',
    parentReflection:
      'What does your child\'s school\'s anti-bullying policy actually say? Spend 5 minutes reading it so you know what to reference if needed.',
    badgeEmoji: '🤝',
    badgeName: 'Upstander',
    targetCount: 3,
    categoryId: 'bullying-social',
  },
  {
    id: 'wc-07',
    weekNumber: 7,
    title: 'Travel Safety Week',
    theme: 'Staying safe on the move',
    icon: '🚌',
    challengeDescription:
      'Plan a short journey together — could be a bus ride, a walk, or a cycle. Let your child be the navigator: they plan the route, identify potential risks, and explain what to do if something goes wrong. Reverse roles halfway.',
    talkingPoints: [
      'What do you do if you miss your stop on the bus and don\'t know where you are?',
      'Is it safe to wear headphones on both ears when walking or cycling? Why not?',
      'What information do you always tell us before you go somewhere independently?',
    ],
    kidMission:
      'Write down your three most common journeys (school, friend\'s house, sports club) and identify the one "risky bit" in each. Share with a parent.',
    parentReflection:
      'At what age and on what routes do you plan to allow your child to travel independently? Have you said this out loud to them? This week, have that conversation.',
    badgeEmoji: '🚌',
    badgeName: 'Safe Traveller',
    targetCount: 3,
    categoryId: 'travel-public',
  },
  {
    id: 'wc-08',
    weekNumber: 8,
    title: 'Trusting Your Gut Week',
    theme: 'That uncomfortable feeling is trying to tell you something',
    icon: '🔮',
    challengeDescription:
      'Play "Gut Check" at dinner. Go around the table and share one moment from the past month where your gut feeling told you something wasn\'t right — and what you did about it. Parents go first to model vulnerability. Discuss: when do we ignore gut feelings and why?',
    talkingPoints: [
      'Can you describe what your gut feeling actually feels like in your body?',
      'Are there people in your life who sometimes make you feel uncomfortable, even if you can\'t say why?',
      'Is it ever rude to trust your gut and walk away? Why or why not?',
    ],
    kidMission:
      'This week, every time you feel uncomfortable, notice it — don\'t push it away. At the end of the week, tell a parent about one time you noticed it.',
    parentReflection:
      'Have you ever explicitly told your child it\'s okay to be rude to an adult if their safety feels at risk? Many children haven\'t heard this said directly.',
    badgeEmoji: '🔮',
    badgeName: 'Gut Instinct Hero',
    targetCount: 3,
    categoryId: 'personal-safety',
  },
  {
    id: 'wc-09',
    weekNumber: 9,
    title: 'Confidence & Assertiveness Week',
    theme: 'Using your voice — even when it\'s hard',
    icon: '🗣️',
    challengeDescription:
      'Practise saying "No" in three situations: politely (to a friend), firmly (to a peer pressuring you), and loudly (to a stranger in danger). Take turns playing both sides. Then discuss: why is "no" so hard to say? What makes it easier?',
    talkingPoints: [
      'Think of a time you said yes when you really wanted to say no. What happened?',
      'What\'s the difference between being assertive and being aggressive?',
      'Who in your life makes it easiest to say no? Who makes it hardest?',
    ],
    kidMission:
      'Say no to one small thing this week that you would normally go along with just to please someone. Notice how it feels.',
    parentReflection:
      'Do you model saying no clearly and without over-explaining? Children learn assertiveness from watching adults. Reflect on one recent situation where you could have been clearer.',
    badgeEmoji: '🗣️',
    badgeName: 'Voice Champion',
    targetCount: 3,
    categoryId: 'confidence-boundaries',
  },
  {
    id: 'wc-10',
    weekNumber: 10,
    title: 'Online Friendships Week',
    theme: 'Real vs. not real — when online feels complicated',
    icon: '🔒',
    challengeDescription:
      'Look at your child\'s top 5 online contacts or gaming friends together. For each one, answer: have you ever met them in person? Do you know their real name? Do you know where they live? Discuss: what information have you shared? Create a "share checklist" together — things never to share online.',
    talkingPoints: [
      'Can someone online be a true friend if you\'ve never met them? What would need to be true?',
      'If someone online asks to move your chat to a different private app, what does that tell you?',
      'Have you ever felt pressured by someone online? What happened?',
    ],
    kidMission:
      'With a parent\'s help, review the privacy settings on one game or app you use regularly. Screenshot the before and after if you make any changes.',
    parentReflection:
      'Do you know the usernames your child uses in games? Have a low-pressure conversation this week — not interrogation — about who they play with online.',
    badgeEmoji: '🔒',
    badgeName: 'Privacy Pro',
    targetCount: 3,
    categoryId: 'online-safety',
  },
  {
    id: 'wc-11',
    weekNumber: 11,
    title: 'Home Alone Safety Week',
    theme: 'What to do when you\'re home without an adult',
    icon: '🏠',
    challengeDescription:
      'Create a "Home Alone Plan" together. Decide: who to call in an emergency, what to do if someone knocks and you don\'t know them, what the rule is about telling friends you\'re home alone, and what a real emergency is versus an inconvenience. Write it up and stick it on the fridge.',
    talkingPoints: [
      'If someone rings the doorbell and won\'t leave, what do you do?',
      'What counts as an emergency when you\'re home alone? How is that different from a problem you can solve yourself?',
      'Would you feel comfortable being home alone for an hour? Two hours? What would make you feel safer?',
    ],
    kidMission:
      'Memorise (or save in your phone) the numbers of: one parent, one other trusted adult, and the emergency services. Test yourself without looking at the end of the week.',
    parentReflection:
      'What age do you believe is appropriate for your child to be home alone? Is this based on their maturity and confidence level, or just their age? Have this conversation with them directly.',
    badgeEmoji: '🏠',
    badgeName: 'Home Hero',
    targetCount: 3,
    categoryId: 'emergency-skills',
  },
  {
    id: 'wc-12',
    weekNumber: 12,
    title: 'Social Media & Self-Image Week',
    theme: 'What you share, what it says, and who sees it',
    icon: '📱',
    challengeDescription:
      'Together, look at a public social media profile (a celebrity, a brand, or a fictional example). Analyse: what does this tell us about this person? What are they trying to say? Then look at your child\'s own profile (if they have one) and ask the same questions. No judgement — just awareness.',
    talkingPoints: [
      'If a stranger saw your profile, what would they think about you? Is that what you want?',
      'Once you post something, can you ever fully take it back? Why does that matter?',
      'Have you ever seen something online that made you feel bad about yourself? What was it?',
    ],
    kidMission:
      'Review your last 10 posts or messages. Is there anything there you\'d be uncomfortable with a teacher, a grandparent, or a future employer seeing? You don\'t have to delete anything — just reflect.',
    parentReflection:
      'What conversations have you had (or avoided) about digital consent and screenshot culture? If your child is over 10, this week is a good time to open one of those doors.',
    badgeEmoji: '📱',
    badgeName: 'Digital Selfie Star',
    targetCount: 3,
    categoryId: 'online-safety',
  },
  {
    id: 'wc-13',
    weekNumber: 13,
    title: 'Family Safety Graduation',
    theme: 'Look how far we\'ve come — the full family check-in',
    icon: '🎓',
    challengeDescription:
      'This is your 3-month graduation challenge. As a family, go back through all 12 previous challenges. For each one, each family member answers: what\'s one thing I still remember? What\'s one thing I\'d do differently now? Then do one short roleplay of the scenario from Week 1 to see how much has changed.',
    talkingPoints: [
      'Which challenge from the last 3 months stuck with you the most? Why?',
      'Is there anything we talked about that made you feel worried or unsafe? Can we talk about it?',
      'What\'s one safety rule you\'d add to our family plan that we haven\'t covered?',
    ],
    kidMission:
      'Write or draw a "Safety Pledge" — three things you promise to do to keep yourself safe. Sign it and ask a parent to co-sign.',
    parentReflection:
      'Over the last 13 weeks, which topic felt the hardest to discuss? What made it hard? Is there more to say?',
    badgeEmoji: '🎓',
    badgeName: 'KidWise Family Graduate',
    targetCount: 3,
  },
];

/**
 * Returns the current weekly challenge based on the user's account creation date.
 * Cycles through all 13 challenges then repeats from week 1.
 */
export function getCurrentWeeklyChallenge(userCreatedAt?: string): WeeklyChallenge {
  const startDate = userCreatedAt ? new Date(userCreatedAt) : new Date();
  const now = new Date();
  const msPerWeek = 7 * 24 * 60 * 60 * 1000;
  const weeksSinceStart = Math.floor((now.getTime() - startDate.getTime()) / msPerWeek);
  const index = weeksSinceStart % WEEKLY_CHALLENGES.length;
  return WEEKLY_CHALLENGES[index];
}
